/**
 * SermonsBlock — Standard plan
 * Sermon library with YouTube/Vimeo embed OR mp3 upload + cover image.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Video, Music, Save, ImageIcon } from "lucide-react";
import FileUpload from "@/components/churchbuilder/FileUpload";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

const blank = () => ({
  id: null,
  title: "",
  speaker: "",
  date: "",
  scripture: "",
  description: "",
  media_type: "youtube",
  media_url: "",
  cover_image_url: "",
  published: true,
});

export default function SermonsBlock({ churchId }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/church-builder/churches/${churchId}/sermons`);
      setItems(data);
    } catch { toast.error("Could not load sermons"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (churchId) load(); /* eslint-disable-next-line */ }, [churchId]);

  const save = async () => {
    if (!editing.title?.trim()) return toast.error("Title required");
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.id;
      if (editing.id) {
        await api.patch(`/church-builder/churches/${churchId}/sermons/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/church-builder/churches/${churchId}/sermons`, payload);
        toast.success("Sermon added");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this sermon?")) return;
    try {
      await api.delete(`/church-builder/churches/${churchId}/sermons/${id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="cb-sermons-block" className="luxury-card rounded-xl p-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="font-serif-display text-2xl">Sermon Library</div>
          <div className="text-xs text-neutral-400 mt-1">YouTube / Vimeo embeds, or upload audio (mp3).</div>
        </div>
        {!editing && (
          <button data-testid="cb-sermon-new" onClick={() => setEditing(blank())} className="btn-gold px-4 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
            <Plus size={12} /> New sermon
          </button>
        )}
      </div>

      {editing && (
        <div className="mt-6 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5 space-y-3" data-testid="cb-sermon-edit">
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="cb-sermon-title" className={inp} placeholder="Title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
            <input className={inp} placeholder="Speaker" value={editing.speaker} onChange={(e) => setEditing({ ...editing, speaker: e.target.value })} />
            <input type="date" className={inp} value={editing.date || ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
            <input className={inp} placeholder="Scripture (e.g. John 3:16)" value={editing.scripture} onChange={(e) => setEditing({ ...editing, scripture: e.target.value })} />
          </div>
          <textarea className={inp} rows={3} placeholder="Description / notes" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />

          <div className="grid sm:grid-cols-3 gap-2">
            {[
              { id: "youtube", label: "YouTube", icon: Video },
              { id: "vimeo", label: "Vimeo", icon: Video },
              { id: "audio", label: "Audio (MP3)", icon: Music },
            ].map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} data-testid={`cb-sermon-type-${t.id}`} type="button" onClick={() => setEditing({ ...editing, media_type: t.id, media_url: "" })}
                  className={`px-3 py-2 rounded text-[10px] tracking-widest uppercase inline-flex items-center justify-center gap-2 border ${editing.media_type === t.id ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-neutral-400"}`}>
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>

          {editing.media_type === "audio" ? (
            <FileUpload churchId={churchId} value={editing.media_url} onChange={(url) => setEditing({ ...editing, media_url: url })} accept="audio/*" label="Audio file" />
          ) : (
            <input className={inp} placeholder={editing.media_type === "youtube" ? "https://youtube.com/watch?v=..." : "https://vimeo.com/..."} value={editing.media_url} onChange={(e) => setEditing({ ...editing, media_url: e.target.value })} />
          )}

          <div>
            <div className="text-[10px] tracking-widest uppercase text-neutral-500 flex items-center gap-2 mb-2"><ImageIcon size={11} /> Cover image (optional)</div>
            <FileUpload churchId={churchId} value={editing.cover_image_url} onChange={(url) => setEditing({ ...editing, cover_image_url: url })} accept="image/*" label="Cover image" />
          </div>

          <label className="inline-flex items-center gap-2 text-xs text-neutral-300">
            <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Published
          </label>

          <div className="flex gap-2 pt-2">
            <button data-testid="cb-sermon-save" disabled={saving} onClick={save} className="btn-gold px-4 py-2 rounded text-[10px] tracking-widest uppercase font-semibold inline-flex items-center gap-2">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
            </button>
            <button onClick={() => setEditing(null)} className="btn-ghost-gold px-4 py-2 rounded text-[10px] tracking-widest uppercase">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {loading ? <Loader2 className="animate-spin text-[#D4AF37]" size={16} /> :
          items.length === 0 ? <div className="text-xs text-neutral-500">No sermons yet.</div> :
          items.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-3 rounded border border-white/10 bg-white/[0.02] p-3" data-testid={`cb-sermon-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white">{s.title}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">{s.speaker || "—"} · {s.date || "—"} · {s.media_type} {s.published ? "" : "· draft"}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(s)} className="text-[#D4AF37] text-[10px] tracking-widest uppercase px-3">Edit</button>
                <button data-testid={`cb-sermon-delete-${s.id}`} onClick={() => remove(s.id)} className="text-neutral-400 hover:text-red-400 p-2"><Trash2 size={14} /></button>
              </div>
            </div>
          ))
        }
      </div>
    </div>
  );
}
